/**
 * GridModel — 表格 ViewModel
 *
 * ## 架构角色
 * 每个表格控件在运行时对应一个 GridModel 实例。
 * 继承 BaseModel，负责表格的数据操作、筛选、排序、列设置、聚合等状态管理。
 *
 * ## 核心功能
 *
 * ### 数据操作
 * - getTableData() / setTableData(records)
 * - insertRow(record) / deleteRow(index) / batchDelete(indices)
 * - updateRow(index, record) / updateCell(index, field, value)
 * - batchUpdate(updates) / getSelectedRows()
 * - getAllGridModels() / getGridModel(code)
 *
 * ### 筛选管理
 * - setFilterRules(rules) / getFilterRules()
 * - addFilterRule(rule) / removeFilterRule(field) / clearFilters()
 *
 * ### 排序管理
 * - setSortState(sortState) / getSortState()
 * - clearSort()
 *
 * ### 列设置管理
 * - setColumnVisible(field, visible) / getColumnVisible(field)
 * - setColumnOrder(fields) / getColumnOrder()
 * - setColumnFrozen(field, frozen) / getColumnFrozen(field)
 *
 * ### 聚合结果
 * - setAggregationResults(results) / getAggregationResults()
 *
 * ### 事件
 * - cellValueChange: 单元格值改变事件
 * - rowAdd / rowDelete / rowUpdate
 * - selectChange: 选中行变化
 *
 * ### 配置持久化
 * - saveTableConfig() / loadTableConfig()
 *
 * ## 使用示例
 * ```js
 * var gridModel = new ttx.models.GridModel({ cItemName: 'table1', cControlType: 'table' });
 * gridModel.setTableData([{ name: '张三', age: 30 }]);
 * gridModel.on('cellValueChange', function(data) { console.log(data); });
 * ```
 */
import _ from 'lodash';

// 存储所有 GridModel 实例
const _gridModelRegistry = {};

ttx.models.register('GridModel', function (modelType) {
  const gridModel = function (data) {
    ttx.models.BaseModel.call(this, data);
    
    // 注册到全局注册表
    const code = data?.cItemName || data?.field || 'default';
    _gridModelRegistry[code] = this;
  };
  gridModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
  gridModel.prototype.modelType = modelType;

  // ===== 静态方法：获取所有表格 VM =====

  /**
   * 获取当前 VM 下所有的表格 GridModel
   * @returns {Object} { tableCode: gridModel }
   */
  gridModel.getAllGridModels = function () {
    return _gridModelRegistry;
  };

  /**
   * 根据编码获取指定表格的 GridModel
   * @param {string} code - 表格编码
   * @returns {GridModel|null}
   */
  gridModel.getGridModel = function (code) {
    return _gridModelRegistry[code] || null;
  };

  /**
   * 销毁所有 GridModel（清理）
   */
  gridModel.destroyAll = function () {
    Object.keys(_gridModelRegistry).forEach(key => {
      delete _gridModelRegistry[key];
    });
  };

  // ===== 数据操作 =====

  /**
   * 获取表格数据
   * @returns {Array} 记录数组
   */
  gridModel.prototype.getTableData = function () {
    return this._get_data('value') || [];
  };

  /**
   * 设置表格数据（全量替换）
   * @param {Array} records - 记录数组
   * @param {boolean} silent - 是否静默设置
   */
  gridModel.prototype.setTableData = function (records, silent) {
    records = records || [];
    this._set_data('value', records, true);
    this._set_data('recordCount', records.length, true);
    if (!silent) {
      this.execute('dataChange', { records, count: records.length });
      this.notifyListeners('dataChange', { records, count: records.length });
    }
  };

  /**
   * 新增一行
   * @param {Object} record - 新记录
   * @param {boolean} silent - 是否静默操作
   * @returns {number} 新增行的索引
   */
  gridModel.prototype.insertRow = function (record, silent) {
    const records = this.getTableData();
    const newRecord = Object.assign({}, record || {});
    records.push(newRecord);
    this.setTableData(records, silent);

    const index = records.length - 1;
    if (!silent) {
      this.execute('rowAdd', { index, record: newRecord });
      this.notifyListeners('rowAdd', { index, record: newRecord });
    }
    return index;
  };

  /**
   * 在指定位置插入一行
   * @param {number} index - 插入位置
   * @param {Object} record - 新记录
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.insertRowAt = function (index, record, silent) {
    const records = this.getTableData();
    const newRecord = Object.assign({}, record || {});
    records.splice(index, 0, newRecord);
    this.setTableData(records, silent);

    if (!silent) {
      this.execute('rowAdd', { index, record: newRecord });
      this.notifyListeners('rowAdd', { index, record: newRecord });
    }
  };

  /**
   * 删除一行
   * @param {number} index - 行索引
   * @param {boolean} silent - 是否静默操作
   * @returns {Object|null} 被删除的记录
   */
  gridModel.prototype.deleteRow = function (index, silent) {
    const records = this.getTableData();
    if (index < 0 || index >= records.length) return null;

    const deletedRecord = records[index];
    records.splice(index, 1);
    this.setTableData(records, silent);

    if (!silent) {
      this.execute('rowDelete', { index, record: deletedRecord });
      this.notifyListeners('rowDelete', { index, record: deletedRecord });
    }
    return deletedRecord;
  };

  /**
   * 批量删除行
   * @param {Array<number>} indices - 行索引数组
   * @param {boolean} silent - 是否静默操作
   * @returns {Array} 被删除的记录数组
   */
  gridModel.prototype.batchDelete = function (indices, silent) {
    if (!indices || !indices.length) return [];

    const records = this.getTableData();
    const sortedIndices = [...indices].sort((a, b) => b - a); // 倒序删除
    const deletedRecords = [];

    sortedIndices.forEach(index => {
      if (index >= 0 && index < records.length) {
        deletedRecords.push(records[index]);
        records.splice(index, 1);
      }
    });

    this.setTableData(records, silent);

    if (!silent) {
      this.execute('batchDelete', { indices: sortedIndices, records: deletedRecords });
      this.notifyListeners('batchDelete', { indices: sortedIndices, records: deletedRecords });
    }
    return deletedRecords;
  };

  /**
   * 更新指定行的数据
   * @param {number} index - 行索引
   * @param {Object} record - 新的行数据
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.updateRow = function (index, record, silent) {
    const records = this.getTableData();
    if (index < 0 || index >= records.length) return;

    const oldRecord = records[index];
    records[index] = Object.assign({}, oldRecord, record);
    this.setTableData(records, silent);

    if (!silent) {
      this.execute('rowUpdate', { index, record: records[index], oldRecord });
      this.notifyListeners('rowUpdate', { index, record: records[index], oldRecord });
    }
  };

  /**
   * 更新指定单元格的值
   * @param {number} index - 行索引
   * @param {string} field - 字段名
   * @param {*} value - 新值
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.updateCell = function (index, field, value, silent) {
    const records = this.getTableData();
    if (index < 0 || index >= records.length) return;

    const oldValue = records[index][field];
    records[index][field] = value;
    this.setTableData(records, silent);

    if (!silent) {
      const changeData = { index, field, value, oldValue, record: records[index] };
      this.execute('cellValueChange', changeData);
      this.notifyListeners('cellValueChange', changeData);
    }
  };

  /**
   * 获取指定行、指定字段的值
   * @param {number} index - 行索引
   * @param {string} field - 字段名
   * @returns {*} 字段值
   */
  gridModel.prototype.getCellValue = function (index, field) {
    const records = this.getTableData();
    if (index < 0 || index >= records.length) return undefined;
    return records[index][field];
  };


  /**
   * 批量更新
   * @param {Array<{index: number, record: Object}>} updates - 更新数组
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.batchUpdate = function (updates, silent) {
    if (!updates || !updates.length) return;

    const records = this.getTableData();
    const changedRecords = [];

    updates.forEach(update => {
      if (update.index >= 0 && update.index < records.length) {
        const oldRecord = records[update.index];
        records[update.index] = Object.assign({}, oldRecord, update.record);
        changedRecords.push(records[update.index]);
      }
    });

    this.setTableData(records, silent);

    if (!silent) {
      this.execute('batchUpdate', { updates, records: changedRecords });
      this.notifyListeners('batchUpdate', { updates, records: changedRecords });
    }
  };

  /**
   * 批量更新单元格
   * @param {Array<{index: number, field: string, value: *}>} updates - 更新数组
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.batchUpdateCells = function (updates, silent) {
    if (!updates || !updates.length) return;

    const records = this.getTableData();
    const changes = [];

    updates.forEach(update => {
      if (update.index >= 0 && update.index < records.length) {
        const oldValue = records[update.index][update.field];
        records[update.index][update.field] = update.value;
        changes.push({
          index: update.index,
          field: update.field,
          value: update.value,
          oldValue,
        });
      }
    });

    this.setTableData(records, silent);

    if (!silent) {
      this.execute('batchCellValueChange', changes);
      this.notifyListeners('batchCellValueChange', changes);
    }
  };

  /**
   * 获取选中行数据
   * @returns {Array} 选中的记录数组
   */
  gridModel.prototype.getSelectedRows = function () {
    const selectedIndices = this.getSelectedIndices();
    const records = this.getTableData();
    return selectedIndices.map(index => records[index]).filter(Boolean);
  };

  /**
   * 获取选中行索引
   * @returns {Array<number>} 选中的索引数组
   */
  gridModel.prototype.getSelectedIndices = function () {
    return this._get_data('selectedIndices') || [];
  };

  /**
   * 设置选中行
   * @param {Array<number>} indices - 选中的索引数组
   * @param {boolean} silent - 是否静默操作
   */
  gridModel.prototype.setSelectedIndices = function (indices, silent) {
    this._set_data('selectedIndices', indices || [], true);
    if (!silent) {
      const selectedRows = this.getSelectedRows();
      this.execute('selectChange', { indices: indices || [], rows: selectedRows });
      this.notifyListeners('selectChange', { indices: indices || [], rows: selectedRows });
    }
  };

  /**
   * 清除选中
   */
  gridModel.prototype.clearSelection = function () {
    this.setSelectedIndices([]);
  };

  /**
   * 获取记录数
   * @returns {number}
   */
  gridModel.prototype.getRecordCount = function () {
    return this.getTableData().length;
  };

  /**
   * 查找记录（按条件）
   * @param {Function} predicate - 条件函数 (record, index) => boolean
   * @returns {Array} 匹配的记录数组
   */
  gridModel.prototype.findRecords = function (predicate) {
    return this.getTableData().filter(predicate);
  };

  /**
   * 查找单条记录
   * @param {Function} predicate - 条件函数
   * @returns {Object|null}
   */
  gridModel.prototype.findOne = function (predicate) {
    return this.getTableData().find(predicate) || null;
  };

  // ===== 筛选管理 =====

  /**
   * 设置筛选规则
   * @param {Array} rules - 筛选规则数组 [{ filterKey: 'field', filteredValues: ['val1', 'val2'] }]
   * @param {boolean} silent - 是否静默设置
   */
  gridModel.prototype.setFilterRules = function (rules, silent) {
    rules = rules || [];
    this._set_data('filterRules', rules, true);
    if (!silent) {
      this.execute('filterChange', rules);
      this.notifyListeners('filterChange', rules);
    }
  };

  /**
   * 获取当前筛选规则
   * @returns {Array}
   */
  gridModel.prototype.getFilterRules = function () {
    return this._get_data('filterRules') || [];
  };

  /**
   * 添加单条筛选规则
   * @param {Object} rule - 筛选规则
   */
  gridModel.prototype.addFilterRule = function (rule) {
    const rules = this.getFilterRules();
    const existingIndex = rules.findIndex(r => r.filterKey === rule.filterKey);
    if (existingIndex >= 0) {
      rules[existingIndex] = rule;
    } else {
      rules.push(rule);
    }
    this.setFilterRules(rules);
  };

  /**
   * 移除指定字段的筛选规则
   * @param {string} field - 字段名
   */
  gridModel.prototype.removeFilterRule = function (field) {
    const rules = this.getFilterRules().filter(r => r.filterKey !== field);
    this.setFilterRules(rules);
  };

  /**
   * 清除所有筛选
   */
  gridModel.prototype.clearFilters = function () {
    this.setFilterRules([]);
  };

  // ===== 排序管理 =====

  /**
   * 设置排序状态
   * @param {Object|Array} sortState - 排序状态
   * @param {boolean} silent - 是否静默设置
   */
  gridModel.prototype.setSortState = function (sortState, silent) {
    this._set_data('sortState', sortState, true);
    if (!silent) {
      this.execute('sortChange', sortState);
      this.notifyListeners('sortChange', sortState);
    }
  };

  /**
   * 获取当前排序状态
   * @returns {Object|Array|null}
   */
  gridModel.prototype.getSortState = function () {
    return this._get_data('sortState') || null;
  };

  /**
   * 清除排序
   */
  gridModel.prototype.clearSort = function () {
    this.setSortState(null);
  };

  // ===== 列设置管理 =====

  /**
   * 设置列可见性
   * @param {string} field - 字段名
   * @param {boolean} visible - 是否可见
   */
  gridModel.prototype.setColumnVisible = function (field, visible) {
    const settings = this.getColumnSettings();
    settings[field] = settings[field] || {};
    settings[field].visible = visible;
    this._set_data('columnSettings', settings, true);
    this.execute('columnSettingChange', { field, visible });
    this.notifyListeners('columnSettingChange', { field, visible });
  };

  /**
   * 获取列可见性
   * @param {string} field - 字段名
   * @returns {boolean}
   */
  gridModel.prototype.getColumnVisible = function (field) {
    const settings = this.getColumnSettings();
    return settings[field]?.visible !== false;
  };

  /**
   * 设置列顺序
   * @param {Array} fields - 字段名数组
   */
  gridModel.prototype.setColumnOrder = function (fields) {
    this._set_data('columnOrder', fields, true);
    this.execute('columnOrderChange', fields);
    this.notifyListeners('columnOrderChange', fields);
  };

  /**
   * 获取列顺序
   * @returns {Array|null}
   */
  gridModel.prototype.getColumnOrder = function () {
    return this._get_data('columnOrder') || null;
  };

  /**
   * 设置列冻结状态
   * @param {string} field - 字段名
   * @param {boolean|string} frozen - 冻结状态
   */
  gridModel.prototype.setColumnFrozen = function (field, frozen) {
    const settings = this.getColumnSettings();
    settings[field] = settings[field] || {};
    settings[field].frozen = frozen;
    this._set_data('columnSettings', settings, true);
    this.execute('columnFrozenChange', { field, frozen });
    this.notifyListeners('columnFrozenChange', { field, frozen });
  };

  /**
   * 获取列冻结状态
   * @param {string} field - 字段名
   * @returns {boolean|string}
   */
  gridModel.prototype.getColumnFrozen = function (field) {
    const settings = this.getColumnSettings();
    return settings[field]?.frozen || false;
  };

  /**
   * 获取所有列设置
   * @returns {Object}
   */
  gridModel.prototype.getColumnSettings = function () {
    return this._get_data('columnSettings') || {};
  };

  /**
   * 设置列宽度
   * @param {string} field - 字段名
   * @param {number} width - 宽度
   */
  gridModel.prototype.setColumnWidth = function (field, width) {
    const settings = this.getColumnSettings();
    settings[field] = settings[field] || {};
    settings[field].width = width;
    this._set_data('columnSettings', settings, true);
    this.execute('columnWidthChange', { field, width });
  };

  /**
   * 获取列宽度
   * @param {string} field - 字段名
   * @returns {number|undefined}
   */
  gridModel.prototype.getColumnWidth = function (field) {
    const settings = this.getColumnSettings();
    return settings[field]?.width;
  };

  // ===== 聚合结果管理 =====

  /**
   * 设置聚合结果
   * @param {Object} results - 聚合结果
   */
  gridModel.prototype.setAggregationResults = function (results) {
    this._set_data('aggregationResults', results, true);
    this.execute('aggregationChange', results);
    this.notifyListeners('aggregationChange', results);
  };

  /**
   * 获取聚合结果
   * @returns {Object}
   */
  gridModel.prototype.getAggregationResults = function () {
    return this._get_data('aggregationResults') || {};
  };

  // ===== 配置持久化 =====

  /**
   * 保存表格配置到缓存
   */
  gridModel.prototype.saveTableConfig = function () {
    const config = {
      filterRules: this.getFilterRules(),
      sortState: this.getSortState(),
      columnSettings: this.getColumnSettings(),
      columnOrder: this.getColumnOrder(),
    };
    const tableCode = this._get_data('cItemName') || this._get_data('field') || 'default';
    this.setCache(`tableConfig_${tableCode}`, config);
    this.execute('configSaved', config);
  };

  /**
   * 从缓存加载表格配置
   * @returns {boolean}
   */
  gridModel.prototype.loadTableConfig = function () {
    const tableCode = this._get_data('cItemName') || this._get_data('field') || 'default';
    const config = this.getCache(`tableConfig_${tableCode}`);
    if (!config) return false;

    if (config.filterRules) this.setFilterRules(config.filterRules, true);
    if (config.sortState) this.setSortState(config.sortState, true);
    if (config.columnSettings) this._set_data('columnSettings', config.columnSettings, true);
    if (config.columnOrder) this._set_data('columnOrder', config.columnOrder, true);

    this.execute('configLoaded', config);
    return true;
  };

  /**
   * 清除保存的表格配置
   */
  gridModel.prototype.clearTableConfig = function () {
    const tableCode = this._get_data('cItemName') || this._get_data('field') || 'default';
    this.setCache(`tableConfig_${tableCode}`, null);
  };

  // ===== 辅助方法 =====

  /**
   * 通知所有监听器
   * @param {string} event - 事件名
   * @param {*} data - 事件数据
   */
  gridModel.prototype.notifyListeners = function (event, data) {
    const listeners = this._get_data('listeners') || [];
    listeners.forEach(function (listener) {
      if (typeof listener.onGridEvent === 'function') {
        listener.onGridEvent(event, data);
      } else if (typeof listener.setState === 'function') {
        const state = {};
        state[event] = data;
        listener.setState(state);
      }
    });
  };

  /**
   * 更新监听器状态（BaseModel 接口实现）
   * @param {Object} listener - 监听器
   */
  gridModel.prototype.updateListenerState = function (listener) {
    const state = {};
    const records = this.getTableData();
    const filterRules = this.getFilterRules();
    const sortState = this.getSortState();
    const columnSettings = this.getColumnSettings();
    const aggregationResults = this.getAggregationResults();
    const selectedIndices = this.getSelectedIndices();

    if (records.length > 0) state.records = records;
    if (filterRules.length > 0) state.filterRules = filterRules;
    if (sortState) state.sortState = sortState;
    if (Object.keys(columnSettings).length > 0) state.columnSettings = columnSettings;
    if (Object.keys(aggregationResults).length > 0) state.aggregationResults = aggregationResults;
    if (selectedIndices.length > 0) state.selectedIndices = selectedIndices;

    if (this.notifyListener(listener, 'setListenerState', state)) return;
    this.notifyListener(listener, 'setState', state);
  };

  /**
   * 销毁（清理注册表）
   */
  gridModel.prototype.destroy = function () {
    const code = this._get_data('cItemName') || this._get_data('field') || 'default';
    if (_gridModelRegistry[code]) {
      delete _gridModelRegistry[code];
    }
  };

  // ========================================================================
  // MDF 兼容 API
  // 规则：保留现有 getTableData/setTableData/updateCell/batchDelete 等自定义 API 不动；
  //       与现有同名 API（getCellValue/getSelectedRows/updateRow/setDirty/setReadOnly）不重复定义；
  //       新增 MDF 标准方法与现有 API 共存。
  // 事件：18 个 MDF 事件通过 on/execute 触发，在对应方法中派发 before/after 钩子。
  // ========================================================================

  // ---- 内部工具：深/浅拷贝 ----
  const _deepCopy = function (data) {
    if (data == null || typeof data !== 'object') return data;
    if (ttx.utils.isArray(data)) return ttx.utils.extend(true, [], data);
    return ttx.utils.extend(true, {}, data);
  };
  const _shallowCopy = function (data) {
    if (ttx.utils.isArray(data)) return data.slice();
    if (data && typeof data === 'object') return ttx.utils.extend({}, data);
    return data;
  };

  // 获取已删除行缓存（带 _status=Delete）
  gridModel.prototype._getDeletedRows = function () {
    return this.getCache('deletedRows') || [];
  };

  // 确保 rowsDataState 与当前记录数同步（每行状态：Insert/Update/Delete/Unchanged）
  gridModel.prototype._ensureRowsDataState = function () {
    const records = this.getTableData();
    let states = this._get_data('rowsDataState') || [];
    if (states.length < records.length) {
      for (let i = states.length; i < records.length; i++) states[i] = ttx.models.DataStates.Unchanged;
    } else if (states.length > records.length) {
      states = states.slice(0, records.length);
    }
    this._set_data('rowsDataState', states, true);
    return states;
  };

  // 标记某行为脏数据（仅非新增行会被标记为 Update）
  gridModel.prototype._markRowDirty = function (rowIndex, state) {
    const states = this._ensureRowsDataState();
    if (rowIndex >= 0 && rowIndex < states.length) {
      if (states[rowIndex] !== ttx.models.DataStates.Insert) {
        states[rowIndex] = state || ttx.models.DataStates.Update;
      }
    }
    this._set_data('rowsDataState', states, true);
  };

  // 获取排序后、筛选前的行（浅拷贝）
  gridModel.prototype._getSortedRows = function () {
    let rows = this.getTableData().slice();
    const sortState = this.getSortState();
    if (sortState) {
      const sorters = ttx.utils.isArray(sortState) ? sortState : [sortState];
      sorters.forEach(function (s) {
        if (!s || !s.field) return;
        const dir = s.direction === 'desc' ? -1 : 1;
        rows.sort(function (a, b) {
          const av = a[s.field], bv = b[s.field];
          if (av == null && bv == null) return 0;
          if (av == null) return -1 * dir;
          if (bv == null) return 1 * dir;
          if (av < bv) return -1 * dir;
          if (av > bv) return 1 * dir;
          return 0;
        });
      });
    }
    return rows;
  };

  // 获取界面显示数据（应用筛选+排序，浅拷贝）
  gridModel.prototype._getDisplayRows = function () {
    let rows = this.getTableData().slice();
    const filterRules = this.getFilterRules();
    if (filterRules && filterRules.length) {
      rows = rows.filter(function (row) {
        return filterRules.every(function (rule) {
          const vals = rule.filteredValues || rule.values || [];
          if (!vals.length) return true;
          return vals.indexOf(row[rule.filterKey]) >= 0;
        });
      });
    }
    const sortState = this.getSortState();
    if (sortState) {
      const sorters = ttx.utils.isArray(sortState) ? sortState : [sortState];
      sorters.forEach(function (s) {
        if (!s || !s.field) return;
        const dir = s.direction === 'desc' ? -1 : 1;
        rows.sort(function (a, b) {
          const av = a[s.field], bv = b[s.field];
          if (av == null && bv == null) return 0;
          if (av == null) return -1 * dir;
          if (bv == null) return 1 * dir;
          if (av < bv) return -1 * dir;
          if (av > bv) return 1 * dir;
          return 0;
        });
      });
    }
    return rows;
  };

  // ---------- 数据获取类 ----------

  // 获取所有数据(dataSource)，含已删除数据（带 _status 字段）
  gridModel.prototype.getData = function () {
    const records = this.getTableData();
    const states = this._ensureRowsDataState();
    const deleted = this._getDeletedRows();
    const result = records.map(function (row, i) {
      const copy = _deepCopy(row);
      copy._status = states[i] || ttx.models.DataStates.Unchanged;
      return copy;
    });
    deleted.forEach(function (row) {
      const copy = _deepCopy(row);
      copy._status = ttx.models.DataStates.Delete;
      result.push(copy);
    });
    return result;
  };

  // 获取页面上显示(筛选排序后)的数据，无 _status 字段
  gridModel.prototype.getAllData = function () {
    return this._getDisplayRows().map(function (row) { return _deepCopy(row); });
  };

  // 深拷贝，获取 dataSource 中数据，不含删除的数据（排序前筛选前）
  gridModel.prototype.getEffeData = function () {
    return this.getTableData().map(function (row) { return _deepCopy(row); });
  };

  // 浅拷贝，获取 dataSource 中数据，不含删除的数据（排序前筛选前）
  gridModel.prototype.getRealData = function () {
    return this.getTableData().slice();
  };

  // 浅拷贝，获取界面显示数据(排序后筛选后)
  gridModel.prototype.getRealViewRows = function () {
    return this._getDisplayRows().slice();
  };

  // 深拷贝，获取所有数据，不含删除数据（排序后筛选前）；传参 false 获取筛选排序前数据（浅拷贝）
  gridModel.prototype.getRows = function (filterSort) {
    if (filterSort === false) {
      return this.getTableData().slice();
    }
    return this._getSortedRows().map(function (row) { return _deepCopy(row); });
  };

  // 浅拷贝，获取所有数据(rows)，不含删除数据（排序后筛选前）
  gridModel.prototype.getRealRows = function () {
    return this._getSortedRows().slice();
  };

  // 深拷贝，按行索引获取某行数据（排序后筛选前）
  gridModel.prototype.getRow = function (index) {
    const rows = this._getSortedRows();
    if (index < 0 || index >= rows.length) return undefined;
    return _deepCopy(rows[index]);
  };

  // 获取脏数据
  gridModel.prototype.getDirtyData = function () {
    const records = this.getTableData();
    const states = this._ensureRowsDataState();
    const result = [];
    records.forEach(function (row, i) {
      if (states[i] && states[i] !== ttx.models.DataStates.Unchanged) {
        const copy = _deepCopy(row);
        copy._status = states[i];
        result.push(copy);
      }
    });
    const deleted = this._getDeletedRows();
    deleted.forEach(function (row) {
      const copy = _deepCopy(row);
      copy._status = ttx.models.DataStates.Delete;
      result.push(copy);
    });
    return result;
  };

  // 获取脏数据行号集合
  gridModel.prototype.getDirtyRowIndexes = function () {
    const states = this._ensureRowsDataState();
    const indexes = [];
    states.forEach(function (s, i) {
      if (s && s !== ttx.models.DataStates.Unchanged) indexes.push(i);
    });
    return indexes;
  };

  // 批量获取栏目信息；不传获取所有栏目
  gridModel.prototype.getColumns = function (fields) {
    const columns = this._get_data('columns') || [];
    if (!fields) return _deepCopy(columns);
    return columns.filter(function (c) {
      return fields.indexOf(c.field || c.cItemName) >= 0;
    }).map(function (c) { return _deepCopy(c); });
  };

  // 获取单列栏目信息
  gridModel.prototype.getColumn = function (field) {
    const columns = this._get_data('columns') || [];
    const col = columns.find(function (c) { return (c.field || c.cItemName) === field; });
    return col ? _deepCopy(col) : null;
  };

  // 判断是否包含该栏目
  gridModel.prototype.hasColumn = function (field) {
    const columns = this._get_data('columns') || [];
    return columns.some(function (c) { return (c.field || c.cItemName) === field; });
  };

  // ---------- 数据设置类 ----------

  // 设置单元格值，触发 before/afterCellValueChange 事件
  // 返回 true 表示写入成功，false 表示被 beforeCellValueChange 中止或索引越界
  // data 格式：{ rowIndex, cellName, value, oldValue }
  gridModel.prototype.setCellValue = function (rowIndex, cellName, value, check, blur) {
    const records = this.getTableData();
    if (rowIndex < 0 || rowIndex >= records.length) return false;
    const oldValue = records[rowIndex][cellName];
    const data = { rowIndex: rowIndex, cellName: cellName, value: value, oldValue: oldValue };
    // beforeCellValueChange 返回 false 中止
    if (!this.execute('beforeCellValueChange', data)) return false;
    records[rowIndex][cellName] = value;
    this._markRowDirty(rowIndex, ttx.models.DataStates.Update);
    this.setTableData(records, true);
    this.execute('afterCellValueChange', data);
    // 兼容现有 cellValueChange 事件
    const changeData = { index: rowIndex, field: cellName, value: value, oldValue: oldValue, record: records[rowIndex] };
    this.execute('cellValueChange', changeData);
    this.notifyListeners('cellValueChange', changeData);
    return true;
  };

  // 批量设置单元格值
  // list: [{rowIndex, cellName, value, check, blur},...]
  gridModel.prototype.setCellValues = function (list) {
    if (!ttx.utils.isArray(list)) return;
    const self = this;
    list.forEach(function (item) {
      if (!item) return;
      self.setCellValue(item.rowIndex, item.cellName, item.value, item.check, item.blur);
    });
  };

  // 设置整列数据的值，触发 before/afterColumnValueChange 事件
  gridModel.prototype.setColumnValue = function (cellName, value, check) {
    const records = this.getTableData();
    const data = { cellName: cellName, value: value };
    if (!this.execute('beforeColumnValueChange', data)) return;
    const self = this;
    records.forEach(function (row, i) {
      row[cellName] = value;
      self._markRowDirty(i, ttx.models.DataStates.Update);
    });
    this.setTableData(records, true);
    this.execute('afterColumnValueChange', data);
  };

  // 远程数据拉取内部实现
  gridModel.prototype._fetchRemote = function (proxyConfig, queryParams, callback) {
    const self = this;
    const url = (typeof proxyConfig === 'string') ? proxyConfig : (proxyConfig && proxyConfig.url);
    if (!url) {
      if (typeof callback === 'function') callback([]);
      return;
    }
    ttx.ajax.xhrUtil({
      url: url,
      method: (proxyConfig && proxyConfig.method) || 'get',
      data: queryParams || {},
      success: function (res) {
        const list = (res && res.data && (res.data.list || res.data.data)) || (res && res.data) || [];
        const rows = ttx.utils.isArray(list) ? list : [];
        self.setTableData(rows);
        self._set_data('rowsDataState', rows.map(function () { return ttx.models.DataStates.Unchanged; }), true);
        self.setCache('deletedRows', []);
        if (typeof callback === 'function') callback(rows);
      },
      fail: function () {
        if (typeof callback === 'function') callback([]);
      }
    });
  };

  // 设置数据源：dataSourceMode 为 local 传真实数据；否则传服务地址、请求参数、回调
  // 触发 beforeSetDataSource/afterSetDataSource 事件
  gridModel.prototype.setDataSource = function (proxyConfig, queryParams, callback) {
    const data = { proxyConfig: proxyConfig, queryParams: queryParams, callback: callback };
    if (!this.execute('beforeSetDataSource', data)) return;
    if (ttx.utils.isArray(proxyConfig)) {
      // local 模式，proxyConfig 为真实数据
      this.setTableData(proxyConfig);
      this._set_data('rowsDataState', proxyConfig.map(function () { return ttx.models.DataStates.Unchanged; }), true);
      this.setCache('deletedRows', []);
      if (typeof callback === 'function') callback(proxyConfig);
    } else {
      // 远程模式
      this._fetchRemote(proxyConfig, queryParams, callback);
    }
    this.execute('afterSetDataSource', data);
  };

  // 同 setDataSource 非 local：远程加载
  gridModel.prototype.load = function (proxyConfig, params, callback) {
    const data = { proxyConfig: proxyConfig, params: params, callback: callback };
    if (!this.execute('beforeSetDataSource', data)) return;
    this._fetchRemote(proxyConfig, params, callback);
    this.execute('afterSetDataSource', data);
  };

  // 设置 grid 栏目，触发 before/afterSetColumns 事件
  gridModel.prototype.setColumns = function (columns, fieldNames) {
    const data = { columns: columns, fieldNames: fieldNames };
    if (!this.execute('beforeSetColumns', data)) return;
    let cols = columns;
    if (fieldNames && ttx.utils.isArray(fieldNames)) {
      cols = columns.filter(function (c) {
        return fieldNames.indexOf(c.field || c.cItemName) >= 0;
      });
    }
    this._set_data('columns', cols, true);
    this.execute('afterSetColumns', data);
    this.notifyListeners('columnsChange', cols);
  };

  // 添加表格栏目（动态列）
  gridModel.prototype.addColumns = function (externalColumns, isShow) {
    if (!ttx.utils.isArray(externalColumns)) return;
    const columns = this._get_data('columns') || [];
    externalColumns.forEach(function (col) {
      if (isShow === false) col.visible = false;
      columns.push(col);
    });
    this._set_data('columns', columns, true);
    this.execute('afterSetColumns', { columns: externalColumns });
    this.notifyListeners('columnsChange', columns);
  };

  // 清除全部数据
  gridModel.prototype.clear = function () {
    this.setTableData([], true);
    this._set_data('rowsDataState', [], true);
    this.setCache('deletedRows', []);
    this.setSelectedIndices([], true);
    this.execute('dataChange', { records: [], count: 0 });
    this.notifyListeners('dataChange', { records: [], count: 0 });
  };

  // ---------- 行操作类 ----------

  // 新增一行（参数重载）：
  // - insertRow(record, silent)：record 为对象时追加（保留现有行为）
  // - insertRow(rowIndex, rowData)：rowIndex 为数字时按位置插入（MDF 兼容）
  gridModel.prototype.insertRow = function (record, silent) {
    if (typeof record === 'number') {
      // MDF 兼容：按位置插入
      const rowIndex = record;
      const rowData = silent;
      this.insertRowAt(rowIndex, rowData, false);
      const states = this._ensureRowsDataState();
      if (rowIndex >= 0 && rowIndex < states.length) states[rowIndex] = ttx.models.DataStates.Insert;
      this._set_data('rowsDataState', states, true);
      return rowIndex;
    }
    // 现有行为：末尾追加
    const records = this.getTableData();
    const newRecord = Object.assign({}, record || {});
    records.push(newRecord);
    this.setTableData(records, silent);
    const states = this._ensureRowsDataState();
    states[records.length - 1] = ttx.models.DataStates.Insert;
    this._set_data('rowsDataState', states, true);
    const index = records.length - 1;
    if (!silent) {
      this.execute('rowAdd', { index: index, record: newRecord });
      this.notifyListeners('rowAdd', { index: index, record: newRecord });
    }
    return index;
  };

  // 批量插行
  gridModel.prototype.insertRows = function (rowIndex, rowDatas) {
    if (!ttx.utils.isArray(rowDatas)) return;
    const records = this.getTableData();
    const insertIndex = (typeof rowIndex === 'number') ? rowIndex : records.length;
    const newRecords = rowDatas.map(function (r) { return Object.assign({}, r || {}); });
    records.splice.apply(records, [insertIndex, 0].concat(newRecords));
    this.setTableData(records, true);
    const states = this._ensureRowsDataState();
    for (let i = insertIndex; i < insertIndex + newRecords.length; i++) {
      states[i] = ttx.models.DataStates.Insert;
    }
    this._set_data('rowsDataState', states, true);
    this.execute('rowAdd', { index: insertIndex, records: newRecords });
    this.notifyListeners('rowAdd', { index: insertIndex, records: newRecords });
  };

  // 增行（末尾追加）
  gridModel.prototype.appendRow = function (rowData) {
    return this.insertRow(rowData, false);
  };

  // 批量更新行数据：indexes=[rowIndex,...], records=[rowData,...]
  gridModel.prototype.updateRows = function (indexes, records) {
    if (!ttx.utils.isArray(indexes) || !ttx.utils.isArray(records)) return;
    const curRecords = this.getTableData();
    const self = this;
    indexes.forEach(function (idx, i) {
      if (idx >= 0 && idx < curRecords.length && records[i]) {
        const oldRecord = curRecords[idx];
        curRecords[idx] = Object.assign({}, oldRecord, records[i]);
        self._markRowDirty(idx, ttx.models.DataStates.Update);
      }
    });
    this.setTableData(curRecords, true);
    this.execute('batchUpdate', { indexes: indexes, records: records });
    this.notifyListeners('batchUpdate', { indexes: indexes, records: records });
  };

  // 删行（别名指向 batchDelete，并记录已删除行用于 getData 含 _status=Delete）
  gridModel.prototype.deleteRows = function (rowIndexes) {
    if (!rowIndexes || !rowIndexes.length) return [];
    const records = this.getTableData();
    const deleted = this._getDeletedRows();
    rowIndexes.forEach(function (idx) {
      if (idx >= 0 && idx < records.length) {
        const copy = _deepCopy(records[idx]);
        copy._status = ttx.models.DataStates.Delete;
        deleted.push(copy);
      }
    });
    this.setCache('deletedRows', deleted);
    return this.batchDelete(rowIndexes, false);
  };

  // 删所有行；isAll=true 删除所有数据，默认 false 删除显示数据
  gridModel.prototype.deleteAllRows = function (isAll) {
    const records = this.getTableData();
    const deleted = this._getDeletedRows();
    let remaining = [];
    if (isAll === true) {
      records.forEach(function (row) {
        const copy = _deepCopy(row);
        copy._status = ttx.models.DataStates.Delete;
        deleted.push(copy);
      });
      remaining = [];
    } else {
      // 删除显示数据，保留被筛选掉的数据
      const filterRules = this.getFilterRules();
      remaining = records.filter(function (row) {
        const isDisplay = !filterRules.length || filterRules.every(function (rule) {
          const vals = rule.filteredValues || rule.values || [];
          if (!vals.length) return true;
          return vals.indexOf(row[rule.filterKey]) >= 0;
        });
        if (isDisplay) {
          const copy = _deepCopy(row);
          copy._status = ttx.models.DataStates.Delete;
          deleted.push(copy);
        }
        return !isDisplay;
      });
    }
    this.setCache('deletedRows', deleted);
    this.setTableData(remaining, true);
    this._ensureRowsDataState();
    this.setSelectedIndices([], true);
    this.execute('batchDelete', { indexes: [], records: deleted });
    this.notifyListeners('batchDelete', { indexes: [], records: deleted });
  };

  // 根据行号获取数据
  gridModel.prototype.getRowsByIndexes = function (rowIndexes) {
    if (!ttx.utils.isArray(rowIndexes)) return [];
    const records = this.getTableData();
    return rowIndexes.map(function (idx) { return records[idx]; }).filter(Boolean).map(function (r) { return _deepCopy(r); });
  };

  // 展开某行嵌套表
  gridModel.prototype.expandRow = function (index) {
    const expanded = this.getCache('expandedRows') || {};
    expanded[index] = true;
    this.setCache('expandedRows', expanded);
    this.execute('rowExpand', { index: index });
    this.notifyListeners('rowExpand', { index: index });
  };

  // 展开所有嵌套表
  gridModel.prototype.expandAllRow = function () {
    const records = this.getTableData();
    const expanded = {};
    records.forEach(function (_, i) { expanded[i] = true; });
    this.setCache('expandedRows', expanded);
    this.execute('rowExpandAll', {});
    this.notifyListeners('rowExpandAll', {});
  };

  // 表格是否显示行展开符号
  gridModel.prototype.setShowExpand = function (value) {
    this._set_data('showExpand', value, true);
    this.notifyListeners('showExpandChange', value);
  };

  // 表格滚动到单元格，返回 {lineno, columnModel, value}
  gridModel.prototype.setScrollCell = function (rowIndex, attr) {
    const records = this.getTableData();
    if (rowIndex < 0 || rowIndex >= records.length) return null;
    const columnModel = this.getColumn(attr);
    const value = records[rowIndex][attr];
    return { lineno: rowIndex, columnModel: columnModel, value: value };
  };

  // 表格行点击事件
  gridModel.prototype.rowClick = function (rowIndex) {
    const records = this.getTableData();
    const record = records[rowIndex];
    this.setCache('focusedRowIndex', rowIndex);
    this.execute('rowClick', { index: rowIndex, record: record });
    this.notifyListeners('rowClick', { index: rowIndex, record: record });
  };

  // ---------- 选择类 ----------

  // 设置选中行，如 model.select([1,2,3])
  // 触发 beforeSelect/afterSelect 事件，data 为 rowIndexes
  gridModel.prototype.select = function (rowIndexes) {
    const data = { rowIndexes: rowIndexes || [] };
    if (!this.execute('beforeSelect', data)) return;
    this._set_data('selectedIndices', rowIndexes || [], true);
    this.execute('afterSelect', data);
    const rows = this.getSelectedRows();
    this.execute('selectChange', { indices: rowIndexes || [], rows: rows });
    this.notifyListeners('selectChange', { indices: rowIndexes || [], rows: rows });
  };

  // 取消选中行，触发 beforeUnselect/afterUnselect 事件
  gridModel.prototype.unselect = function (rowIndexes) {
    const data = { rowIndexes: rowIndexes || [] };
    if (!this.execute('beforeUnselect', data)) return;
    let current = this.getSelectedIndices();
    (rowIndexes || []).forEach(function (idx) {
      const i = current.indexOf(idx);
      if (i >= 0) current.splice(i, 1);
    });
    this._set_data('selectedIndices', current, true);
    this.execute('afterUnselect', data);
    const rows = this.getSelectedRows();
    this.execute('selectChange', { indices: current, rows: rows });
    this.notifyListeners('selectChange', { indices: current, rows: rows });
  };

  // 选中所有行
  gridModel.prototype.selectAll = function () {
    const records = this.getTableData();
    const indexes = records.map(function (_, i) { return i; });
    this.select(indexes);
  };

  // 取消选中所有行
  gridModel.prototype.unselectAll = function () {
    this.unselect(this.getSelectedIndices());
  };

  // 获取已选中行行号
  gridModel.prototype.getSelectedRowIndexes = function () {
    return this.getSelectedIndices();
  };

  // ---------- 状态类 ----------

  // 获取行状态
  gridModel.prototype.getRowState = function (rowIndex, name) {
    const rowStates = this._get_data('rowStates') || {};
    const rowState = rowStates[rowIndex] || {};
    return rowState[name];
  };

  // 设置当前行状态(禁用、只读、样式)，触发 before/afterRowStateChange 事件
  gridModel.prototype.setRowState = function (rowIndex, name, value) {
    const data = { rowIndex: rowIndex, name: name, value: value };
    if (!this.execute('beforeRowStateChange', data)) return;
    const rowStates = this._get_data('rowStates') || {};
    const rowState = rowStates[rowIndex] || (rowStates[rowIndex] = {});
    rowState[name] = value;
    this._set_data('rowStates', rowStates, true);
    this.execute('afterRowStateChange', data);
    this.notifyListeners('rowStateChange', data);
  };

  // 批量设置行状态：[{rowIndex, propertyName, value},...]
  gridModel.prototype.setRowStates = function (list) {
    if (!ttx.utils.isArray(list)) return;
    const self = this;
    list.forEach(function (item) {
      if (!item) return;
      self.setRowState(item.rowIndex, item.propertyName, item.value);
    });
  };

  // 获取列状态
  gridModel.prototype.getColumnState = function (cellName, name) {
    const columnStates = this._get_data('columnStates') || {};
    const colState = columnStates[cellName] || {};
    return colState[name];
  };

  // 设置列状态，触发 before/afterColumnStateChange 事件
  gridModel.prototype.setColumnState = function (cellName, name, value) {
    const data = { cellName: cellName, name: name, value: value };
    if (!this.execute('beforeColumnStateChange', data)) return;
    const columnStates = this._get_data('columnStates') || {};
    const colState = columnStates[cellName] || (columnStates[cellName] = {});
    colState[name] = value;
    this._set_data('columnStates', columnStates, true);
    this.execute('afterColumnStateChange', data);
    this.notifyListeners('columnStateChange', data);
  };

  // 批量设置列状态：[{cellName, name, value},...]
  gridModel.prototype.setColumnStates = function (list) {
    if (!ttx.utils.isArray(list)) return;
    const self = this;
    list.forEach(function (item) {
      if (!item) return;
      self.setColumnState(item.cellName, item.name, item.value);
    });
  };

  // 获取单元格状态
  gridModel.prototype.getCellState = function (rowIndex, cellName, name) {
    const cellStates = this._get_data('cellStates') || {};
    const key = rowIndex + '_' + cellName;
    const cellState = cellStates[key] || {};
    return cellState[name];
  };

  // 设置单元格状态，触发 before/afterCellStateChange 事件
  gridModel.prototype.setCellState = function (rowIndex, cellName, propertyName, value) {
    const data = { rowIndex: rowIndex, cellName: cellName, propertyName: propertyName, value: value };
    if (!this.execute('beforeCellStateChange', data)) return;
    const cellStates = this._get_data('cellStates') || {};
    const key = rowIndex + '_' + cellName;
    const cellState = cellStates[key] || (cellStates[key] = {});
    cellState[propertyName] = value;
    this._set_data('cellStates', cellStates, true);
    this.execute('afterCellStateChange', data);
    this.notifyListeners('cellStateChange', data);
  };

  // 批量设置单元格状态：[{rowIndex, cellName, propertyName, value},...]
  gridModel.prototype.setCellStates = function (list) {
    if (!ttx.utils.isArray(list)) return;
    const self = this;
    list.forEach(function (item) {
      if (!item) return;
      self.setCellState(item.rowIndex, item.cellName, item.propertyName, item.value);
    });
  };

  // 设置焦点行
  gridModel.prototype.setFocusedRowIndex = function (index) {
    this.setCache('focusedRowIndex', index);
    this.execute('focusedRowChange', { index: index });
    this.notifyListeners('focusedRowChange', { index: index });
  };

  // 获取焦点行（如果不存在则添加）
  gridModel.prototype.getFocusedRowIndex = function () {
    let index = this.getCache('focusedRowIndex');
    if (index == null || index < 0) {
      const records = this.getTableData();
      if (records.length) {
        index = 0;
        this.setCache('focusedRowIndex', index);
      }
    }
    return index == null ? -1 : index;
  };

  // 校验函数：合法性、必输项
  gridModel.prototype.validate = function () {
    const records = this.getTableData();
    const columns = this._get_data('columns') || [];
    const errors = [];
    records.forEach(function (row, i) {
      columns.forEach(function (col) {
        const field = col.field || col.cItemName;
        // bIsNull=false 表示必输
        if (col.bIsNull === false && ttx.utils.isEmpty(row[field])) {
          errors.push({ rowIndex: i, cellName: field, message: (col.cShowCaption || field) + '不能为空' });
        }
      });
    });
    if (errors.length) {
      this.execute('validateError', errors);
      return false;
    }
    return true;
  };

  // ---------- 分页类 ----------

  // 设置分页页大小
  gridModel.prototype.setPageSize = function (pageSize) {
    this._set_data('pageSize', pageSize, true);
    this.execute('pageSizeChange', pageSize);
    this.notifyListeners('pageSizeChange', pageSize);
  };

  // 设置分页页码
  gridModel.prototype.setPageIndex = function (pageIndex) {
    this._set_data('pageIndex', pageIndex, true);
    this.execute('pageIndexChange', pageIndex);
    this.notifyListeners('pageIndexChange', pageIndex);
  };

  // 获取当前分页页大小
  gridModel.prototype.getPageSize = function () {
    return this._get_data('pageSize');
  };

  // 获取当前页码
  gridModel.prototype.getPageIndex = function () {
    return this._get_data('pageIndex');
  };

  return gridModel;
});
